import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Blocks, Check, ChevronDown, Code, Grid2x2, Loader2, Plus, Settings, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { CodeEditor } from '../../components/CodeEditor'
import { LayoutComponentsEditor } from '../../components/LayoutComponentsEditor'
import { VariableReference } from '../../components/VariableReference'
import { HANDLEBARS_VARIABLE_GROUPS } from '../../lib/adCodeVariables'
import {
  StatsBar,
  StatsBarId,
  StatsBarActivity,
} from '../../components/StatsBar'
import { AppShell } from '../../components/AppShell'
import { FormSkeleton } from '../../components/FormSkeleton'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { AuthService } from '@/services/core/auth'
import { LayoutsApi, LayoutInput, Layout, type LayoutComponent } from '@/services/sites/layoutsApi'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { formatDate } from '../../utils/format'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'
import { ValidationWarnings } from '@/components/ValidationWarnings'

// ── Constants ───────────────────────────────────────────────────────────────

const LAYOUT_TYPE_OPTIONS = [
  'UNKNOWN', 'FLASH', 'IMAGE', 'THIRD_PARTY', 'VAST', 'NATIVE', 'MULTI_FORMAT',
]

const TYPE_OPTIONS = ['INTERNAL', 'CUSTOM', 'GLOBAL', 'NETWORK']

// ── Inner form component ────────────────────────────────────────────────────

interface LayoutEditFormProps {
  layout: Layout | null
  id: string
  isNew: boolean
}

function LayoutEditForm({ layout, id, isNew }: LayoutEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [labelInput, setLabelInput] = useState('')

  const form = useForm({
    defaultValues: {
      name: layout?.name ?? '',
      description: layout?.description ?? '',
      renderTemplate: layout?.renderTemplate ?? '',
      layoutComponents: layout?.layoutComponents ?? [] as LayoutComponent[],
      layoutType: layout?.layoutType ?? '',
      type: layout?.type ?? '',
      political: layout?.political ?? false,
      fullPage: layout?.fullPage ?? false,
      verifiedSafe: layout?.verifiedSafe ?? false,
      labels: layout?.labels ?? [] as string[],
    },
    onSubmit: ({ value }) => {
      const input: LayoutInput = {
        name: value.name,
        description: value.description || undefined,
        objectState: (layout?.objectState ?? 'ACTIVE') as ObjectState,
        renderTemplate: value.renderTemplate,
        layoutComponents: value.layoutComponents,
        layoutType: value.layoutType || undefined,
        type: value.type || undefined,
        political: value.political,
        fullPage: value.fullPage,
        verifiedSafe: value.verifiedSafe,
        labels: value.labels.length ? value.labels : undefined,
      }
      mutation.mutate(input)
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => LayoutsApi.update(id, { name: layout?.name ?? '', objectState }),
    ['layout', id],
    ['layouts']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const mutation = useMutation({
    mutationFn: (input: LayoutInput) =>
      isNew ? LayoutsApi.create(id, input) : LayoutsApi.update(id, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['layout', id], data)
      queryClient.setQueryData(['layouts'], (old: Layout[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((l) => l.id === data.id)
        return idx >= 0 ? old.map((l) => (l.id === data.id ? data : l)) : [...old, data]
      })
      markClean()
      if (isNew) {
        queryClient.invalidateQueries({ queryKey: ['layouts'] })
        // Drop ?new=true so a reload lands on the saved layout.
        navigate(`/admin/layouts/${data.id}`, { replace: true })
      }
      toast.success(t(isNew ? 'common.created' : 'common.saved'), {
        description: t(isNew ? 'layouts.createdSuccess' : 'layouts.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const fallback = t(isNew ? 'layouts.createFailed' : 'layouts.saveFailed')
      const summary = error instanceof ApiError ? error.message : fallback
      toast.error(summary, { description: apiErrorDescription(error), duration: 5000 })
    },
  })

  const crumbs = [
    { label: t('layouts.title'), href: '/admin/layouts' },
    { label: isNew ? t('layouts.newTitle') : (layout?.name ?? t('layouts.editTitle')) },
  ]

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={layout?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.layout')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('layouts.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {layout && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={layout!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {layout && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={layout.id} />,
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(layout.createTime)}
                    updated={formatDate(layout.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="ad-edit-columns">
          <div className="ad-edit-columns__info">

            {/* Details card */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Grid2x2} />
                  {t('layouts.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('common.description')}</label>
                    <form.Field name="description">
                      {(field) => (
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('layouts.descriptionPlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>

                  <div className="p-field">
                    <label>{t('layouts.layoutType')}</label>
                    <form.Field name="layoutType">
                      {(field) => (
                        <Select label={t('layouts.layoutType')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__none__' ? '' : (v ?? ''))}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('layouts.selectLayoutType')}>
                              {field.state.value || t('layouts.selectLayoutType')}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {LAYOUT_TYPE_OPTIONS.map((o) => (
                              <SelectItem key={o} value={o}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>

                  <div className="p-field">
                    <label>{t('common.type')}</label>
                    <form.Field name="type">
                      {(field) => (
                        <Select label={t('common.type')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__none__' ? '' : (v ?? ''))}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('layouts.selectType')}>
                              {field.state.value || t('layouts.selectType')}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {TYPE_OPTIONS.map((o) => (
                              <SelectItem key={o} value={o}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>

                  <div className="p-field full">
                    <label>{t('common.labels')}</label>
                    <form.Field name="labels">
                      {(field) => (
                        <div className="flex flex-wrap gap-1.5 rounded-(--ad-radius-sm) border border-(--ad-border) bg-(--ad-surface) p-2 min-h-9.5">
                          {field.state.value.map((tag, i) => (
                            <span key={i} className="inline-flex items-center gap-1 rounded-full bg-(--ad-badge-blue-bg) text-(--ad-badge-blue) border border-(--ad-badge-blue-border) px-2 py-0.5 text-xs font-medium">
                              {tag}
                              <button
                                type="button"
                                onClick={() => field.handleChange(field.state.value.filter((_, j) => j !== i))}
                                className="hover:text-(--ad-danger)"
                              >
                                <X className="size-3" />
                              </button>
                            </span>
                          ))}
                          <input
                            className="flex-1 min-w-30 bg-transparent outline-none text-sm text-(--ad-text) placeholder:text-(--ad-text-faint)"
                            value={labelInput}
                            onChange={(e) => setLabelInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && labelInput.trim()) {
                                e.preventDefault()
                                field.handleChange([...field.state.value, labelInput.trim()])
                                setLabelInput('')
                              }
                              if (e.key === 'Backspace' && !labelInput && field.state.value.length > 0) {
                                field.handleChange(field.state.value.slice(0, -1))
                              }
                            }}
                            placeholder={t('layouts.labelsPlaceholder')}
                          />
                        </div>
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Template editor card */}
            <Card tone="creative">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Code} />
                  {t('layouts.renderTemplate')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <form.Field name="renderTemplate">
                  {(field) => (
                    <CodeEditor
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      variables="layoutTemplate"
                    />
                  )}
                </form.Field>
              </CardContent>
            </Card>

            {/* Components the creatives fill in, referenced from the template */}
            <Card tone="creative">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Blocks} />
                  {t('layouts.components')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form.Field name="layoutComponents">
                  {(field) => (
                    <LayoutComponentsEditor
                      value={field.state.value}
                      onChange={(value) => field.handleChange(value)}
                      disabled={mutation.isPending}
                    />
                  )}
                </form.Field>
              </CardContent>
            </Card>

            {/* Variable reference accordion */}
            <Card tone="insight">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={ChevronDown} />
                  {t('layouts.variableReference')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <VariableReference groups={HANDLEBARS_VARIABLE_GROUPS} />
              </CardContent>
            </Card>

          </div>

          {/* Settings sidebar */}
          <Card style={{ minWidth: 260, maxWidth: 320, alignSelf: 'start' }}>
            <CardHeader>
              <CardTitle>
                <CardIcon icon={Settings} />
                {t('layouts.settings')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4">
                <form.Field name="political">
                  {(field) => (
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="political-switch">{t('layouts.political')}</Label>
                      <Switch
                        id="political-switch"
                        checked={field.state.value}
                        onCheckedChange={(v) => field.handleChange(v)}
                      />
                    </div>
                  )}
                </form.Field>

                <form.Field name="fullPage">
                  {(field) => (
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="fullpage-switch">{t('layouts.fullPage')}</Label>
                      <Switch
                        id="fullpage-switch"
                        checked={field.state.value}
                        onCheckedChange={(v) => field.handleChange(v)}
                      />
                    </div>
                  )}
                </form.Field>

                <form.Field name="verifiedSafe">
                  {(field) => (
                    <div className="flex items-center justify-between gap-3">
                      <Label htmlFor="verified-switch">{t('layouts.verifiedSafe')}</Label>
                      <Switch
                        id="verified-switch"
                        checked={field.state.value}
                        onCheckedChange={(v) => field.handleChange(v)}
                      />
                    </div>
                  )}
                </form.Field>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}>
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <Loader2 className="size-4 animate-spin" />
                {t(isNew ? 'common.creating' : 'common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/layouts')}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('layouts.createLayout') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function LayoutEditPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const { data: layout, isLoading, isError } = useQuery({
    queryKey: ['layout', id],
    queryFn: () => LayoutsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('layouts.title'), href: '/admin/layouts' },
    { label: isNew ? t('layouts.newTitle') : t('layouts.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return <LayoutEditForm key={id} layout={layout ?? null} id={id!} isNew={isNew} />
}
